/**
 * Regras de tela da aba Forecast e vendas. O backend entrega o forecast (demanda do
 * Balanceamento), o faturado (nota fiscal válida) por mês de produção e os rankings de UF;
 * aqui só se recorta por mês e medida (unidades ou R$) e se monta o que cada bloco desenha.
 * Tudo puro, para teste.
 */
import type { QtdValor, VendasMes, VendasPayload, VendasUf } from '@/types/dashboards';
import { fmtBRL, fmtBRLCompacto, fmtCompacto, fmtUn } from './painelFormat';

/** Unidades ou reais: a mesma tela responde "quanto saiu" e "quanto entrou". */
export type Medida = 'qtd' | 'valor';

export function fmtMedida(v: number | null, medida: Medida, compacto = true): string {
  if (v === null || !Number.isFinite(v)) return '—';
  if (medida === 'valor') return compacto ? fmtBRLCompacto(v) : fmtBRL(v);
  return compacto ? fmtCompacto(v) : fmtUn(v);
}

export function mesDoPayload(p: VendasPayload, chave: string): VendasMes | undefined {
  return p.meses.find((m) => m.key === chave);
}

function soma(valores: Record<string, QtdValor> | undefined, medida: Medida): number {
  return Object.values(valores ?? {}).reduce((total, v) => total + v[medida], 0);
}

const pct = (parte: number | null, todo: number | null): number | null =>
  parte === null || !todo ? null : (parte / todo) * 100;

/** Forecast proporcional aos dias que já passaram: é contra ele que o mês aberto se mede. */
function esperadoAteCorte(m: VendasMes, forecast: number | null): number | null {
  if (forecast === null || m.dias === 0) return null;
  return (forecast * m.dias_decorridos) / m.dias;
}

// ---------------------------------------------------------------- série mensal

export interface PontoMensal {
  key: string;
  label: string;
  /** `null` quando o Balanceamento não tem forecast no mês. */
  forecast: number | null;
  /** `null` no mês futuro (ainda não há o que faturar). */
  faturado: number | null;
  esperado: number | null;
  pctDoForecast: number | null;
  emAndamento: boolean;
  futuro: boolean;
}

export function serieMensal(p: VendasPayload, medida: Medida): PontoMensal[] {
  return p.meses.map((m) => {
    const forecast = m.tem_forecast ? soma(p.forecast.meses[m.key], medida) : null;
    const faturado = m.futuro ? null : soma(p.faturado[m.key], medida);
    return {
      key: m.key,
      label: m.label,
      forecast,
      faturado,
      esperado: m.em_andamento ? esperadoAteCorte(m, forecast) : null,
      pctDoForecast: pct(faturado, forecast),
      emAndamento: m.em_andamento,
      futuro: m.futuro,
    };
  });
}

// ---------------------------------------------------------------- UF

const UFS: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AM: 'Amazonas',
  AP: 'Amapá',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MG: 'Minas Gerais',
  MS: 'Mato Grosso do Sul',
  MT: 'Mato Grosso',
  PA: 'Pará',
  PB: 'Paraíba',
  PE: 'Pernambuco',
  PI: 'Piauí',
  PR: 'Paraná',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RO: 'Rondônia',
  RR: 'Roraima',
  RS: 'Rio Grande do Sul',
  SC: 'Santa Catarina',
  SE: 'Sergipe',
  SP: 'São Paulo',
  TO: 'Tocantins',
  EX: 'Exportação',
};

/** `GO` → `Goiás`; `EX` → `Exportação`; vazio → `Sem UF` (nota sem cabeçalho na réplica). */
export function nomeUf(sigla: string): string {
  if (!sigla) return 'Sem UF';
  return UFS[sigla] ?? sigla;
}

/** Rótulo curto do eixo: a sigla, com `EX` e vazio legíveis. */
export function siglaUf(sigla: string): string {
  if (!sigla) return 'Sem UF';
  return sigla === 'EX' ? 'Export.' : sigla;
}

export interface LinhaUf {
  uf: string;
  rotulo: string;
  nome: string;
  valor: number;
  /** Participação no total do recorte, em %. */
  share: number;
  /** Quantas UFs foram somadas na linha "Outras" (0 nas demais). */
  agrupadas: number;
}

/**
 * Ranking de UF de um recorte (`janela` = últimos 12 meses, ou a chave de um mês), na
 * medida escolhida. Além do `top`, o resto vira uma linha "Outras" — nunca uma nona cor.
 */
export function rankingUf(p: VendasPayload, escopo: 'janela' | string, medida: Medida, top = 10): LinhaUf[] {
  const fonte: VendasUf[] = escopo === 'janela' ? p.ufs.total : (p.ufs.por_mes[escopo] ?? []);
  const ordenado = [...fonte].filter((u) => u[medida] > 0).sort((a, b) => b[medida] - a[medida]);
  const total = ordenado.reduce((s, u) => s + u[medida], 0);
  if (total === 0) return [];
  const linha = (uf: string, valor: number, agrupadas = 0): LinhaUf => ({
    uf,
    rotulo: agrupadas ? `Outras (${agrupadas})` : siglaUf(uf),
    nome: agrupadas ? `${agrupadas} UFs somadas` : nomeUf(uf),
    valor,
    share: (valor / total) * 100,
    agrupadas,
  });
  const linhas = ordenado.slice(0, top).map((u) => linha(u.uf, u[medida]));
  const resto = ordenado.slice(top);
  if (resto.length > 0) linhas.push(linha('*', resto.reduce((s, u) => s + u[medida], 0), resto.length));
  return linhas;
}

/** A UF que mais comprou o SKU nos 12 meses, com a participação dela. */
export function ufPrincipal(p: VendasPayload, codigo: string, medida: Medida): { uf: string; share: number } | null {
  const ufs = p.ufs.por_item[codigo] ?? [];
  const total = ufs.reduce((s, u) => s + u[medida], 0);
  if (total <= 0) return null;
  const maior = ufs.reduce((a, b) => (b[medida] > a[medida] ? b : a));
  return { uf: maior.uf, share: (maior[medida] / total) * 100 };
}

// ---------------------------------------------------------------- KPIs

export interface KpisVendas {
  forecast: number | null;
  faturado: number | null;
  pctDoForecast: number | null;
  esperado: number | null;
  aderenciaRitmo: number | null;
  emAndamento: boolean;
  futuro: boolean;
  dias: number;
  diasDecorridos: number;
  skusForecast: number;
  skusFaturados: number;
  /** SKUs do forecast do mês que já têm nota. */
  skusForecastVendidos: number;
  /** SKUs com nota no mês que não estão no forecast (só conta em mês com forecast). */
  skusForaDoForecast: number;
  ufs: number;
  ufPrincipal: LinhaUf | null;
}

export function kpisDoMes(p: VendasPayload, mes: string, medida: Medida): KpisVendas | null {
  const m = mesDoPayload(p, mes);
  if (!m) return null;
  const ponto = serieMensal(p, medida).find((s) => s.key === mes);
  const resumo = p.resumo_mensal.find((r) => r.mes === mes);
  const ranking = rankingUf(p, mes, medida, 99);
  const esperado = m.em_andamento ? (ponto?.esperado ?? null) : (ponto?.forecast ?? null);
  const previstos = new Set(Object.keys(p.forecast.meses[mes] ?? {}));
  const vendidos = Object.entries(p.faturado[mes] ?? {})
    .filter(([, v]) => v.qtd > 0)
    .map(([codigo]) => codigo);
  return {
    forecast: ponto?.forecast ?? null,
    faturado: ponto?.faturado ?? null,
    pctDoForecast: ponto?.pctDoForecast ?? null,
    esperado,
    aderenciaRitmo: m.futuro ? null : pct(ponto?.faturado ?? null, esperado),
    emAndamento: m.em_andamento,
    futuro: m.futuro,
    dias: m.dias,
    diasDecorridos: m.dias_decorridos,
    skusForecast: resumo?.skus_forecast ?? 0,
    skusFaturados: resumo?.skus_faturados ?? 0,
    skusForecastVendidos: vendidos.filter((c) => previstos.has(c)).length,
    skusForaDoForecast: m.tem_forecast ? vendidos.filter((c) => !previstos.has(c)).length : 0,
    ufs: ranking.length,
    ufPrincipal: ranking[0] ?? null,
  };
}

// ---------------------------------------------------------------- tabela por SKU

export type SituacaoVenda = 'normal' | 'sem_venda' | 'fora_do_forecast' | 'futuro';

export interface LinhaSkuVendas {
  codigo: string;
  codigoDatasul: string | null;
  descricao: string;
  categoria: string;
  forecast: number | null;
  faturado: number | null;
  pctDoForecast: number | null;
  /** Forecast dos próximos meses (mesma ordem de `proximos`); `null` = sem forecast do SKU. */
  proximos: (number | null)[];
  ufPrincipal: { uf: string; share: number } | null;
  situacao: SituacaoVenda;
}

export interface TabelaSkuVendas {
  proximos: VendasMes[];
  linhas: LinhaSkuVendas[];
}

/** Até quantos meses à frente a tabela abre em colunas (o Manus mostrava ago–dez). */
export const MAX_PROXIMOS = 4;

function situacao(m: VendasMes, forecast: number | null, faturado: number | null): SituacaoVenda {
  if (m.futuro) return 'futuro';
  if (forecast && !faturado) return 'sem_venda';
  if (!forecast && faturado && m.tem_forecast) return 'fora_do_forecast';
  return 'normal';
}

function valorOuNull(valores: Record<string, QtdValor> | undefined, codigo: string, medida: Medida): number | null {
  const v = valores?.[codigo];
  return v ? v[medida] : null;
}

export function tabelaSku(p: VendasPayload, mes: string, medida: Medida): TabelaSkuVendas {
  const m = mesDoPayload(p, mes);
  if (!m) return { proximos: [], linhas: [] };
  const proximos = p.meses.filter((x) => x.key > mes && x.tem_forecast).slice(0, MAX_PROXIMOS);
  const codigos = new Set<string>([
    ...Object.keys(p.forecast.meses[mes] ?? {}),
    ...Object.keys(p.faturado[mes] ?? {}),
    ...proximos.flatMap((x) => Object.keys(p.forecast.meses[x.key] ?? {})),
  ]);
  const linhas = [...codigos].map((codigo): LinhaSkuVendas => {
    const item = p.itens[codigo];
    const forecast = valorOuNull(p.forecast.meses[mes], codigo, medida);
    const faturado = m.futuro ? null : (valorOuNull(p.faturado[mes], codigo, medida) ?? 0);
    return {
      codigo,
      codigoDatasul: item?.codigo_datasul ?? null,
      descricao: item?.descricao ?? '',
      categoria: item?.categoria ?? 'Outros',
      forecast,
      faturado,
      pctDoForecast: pct(faturado, forecast),
      proximos: proximos.map((x) => valorOuNull(p.forecast.meses[x.key], codigo, medida)),
      ufPrincipal: ufPrincipal(p, codigo, medida),
      situacao: situacao(m, forecast, faturado),
    };
  });
  linhas.sort((a, b) => (b.forecast ?? 0) - (a.forecast ?? 0) || (b.faturado ?? 0) - (a.faturado ?? 0));
  return { proximos, linhas };
}

export interface DivergenciaVenda {
  categoria: string;
  semVenda: LinhaSkuVendas[];
  foraDoForecast: LinhaSkuVendas[];
}

/** Primeira palavra da descrição, sem acento: `FISIOLOGICO 0,9% 100 ML-SF` → `FISIOLOGICO`. */
function produtoBase(descricao: string): string {
  const palavra = descricao.trim().split(/\s+/)[0] ?? '';
  return palavra.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
}

/**
 * Mesmo produto e formato em que o forecast cita um SKU que não vendeu e outro SKU vendeu
 * fora do forecast: quase sempre é o código trocado (o 1110715 × 1110716). O produto entra
 * na chave porque só o formato junta coisa diferente — a água de exportação sem embarque no
 * mês não é "troca de código" do cloreto que vendeu no mercado interno.
 */
export function divergenciasDeCodigo(linhas: LinhaSkuVendas[]): DivergenciaVenda[] {
  const grupos = new Map<string, DivergenciaVenda>();
  for (const l of linhas) {
    if (l.situacao !== 'sem_venda' && l.situacao !== 'fora_do_forecast') continue;
    const chave = `${l.categoria}|${produtoBase(l.descricao)}`;
    const d = grupos.get(chave) ?? { categoria: l.categoria, semVenda: [], foraDoForecast: [] };
    (l.situacao === 'sem_venda' ? d.semVenda : d.foraDoForecast).push(l);
    grupos.set(chave, d);
  }
  return [...grupos.values()].filter((d) => d.semVenda.length > 0 && d.foraDoForecast.length > 0);
}
